<template>
  <crud-modal
    :show="show"
    title="Cambiar Contraseña de Usuario"
    size="md"
    :loading="loading"
    save-text="Guardar Contraseña"
    @save="guardarPassword"
    @close="$emit('close')"
  >
    <div class="password-modal-content">
      <div v-if="user" class="alert alert-light border mb-3 py-2 px-3 small">
        <i class="bi bi-person-fill text-muted mr-1"></i>
        Usuario: <strong>{{ user.name }}</strong> ({{ user.email || 'Sin correo' }})
      </div>

      <div class="form-group">
        <label class="font-weight-bold small text-muted mb-1">Nueva Contraseña</label>
        <div class="input-group">
          <input
            class="form-control"
            :class="{
              'is-invalid': errors && errors.errors && errors.errors.password,
              'is-valid': passwordStrength && passwordStrength.isValid
            }"
            :type="passwordFieldType"
            placeholder="Mínimo 8 caracteres"
            v-model="password"
            @input="checkPasswordStrength"
          />
          <div class="input-group-append">
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="togglePasswordVisibility"
              title="Mostrar u ocultar contraseña"
            >
              <i :class="passwordFieldType === 'text' ? 'bi-eye-slash-fill' : 'bi-eye-fill'"></i>
            </button>
          </div>
          <div
            v-if="errors && errors.errors && errors.errors.password"
            class="invalid-feedback"
          >
            <div v-for="(err, idx) of errors.errors.password" :key="idx">{{ err }}</div>
          </div>
        </div>
        <small class="form-text text-muted">
          La contraseña debe tener al menos 8 caracteres, incluyendo mayúsculas, minúsculas, números y caracteres especiales.
        </small>
        <div v-if="passwordStrength" class="password-strength mt-2 small font-weight-bold">
          <span :class="passwordStrength.isValid ? 'text-success' : 'text-danger'">
            <i :class="passwordStrength.isValid ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'"></i>
            {{ passwordStrength.message }}
          </span>
        </div>
      </div>

      <div class="form-group mb-0">
        <label class="font-weight-bold small text-muted mb-1">Repetir Contraseña</label>
        <div class="input-group">
          <input
            class="form-control"
            :class="{
              'is-invalid': (errors && errors.errors && errors.errors.password_confirmation) || (password_confirmation && password !== password_confirmation)
            }"
            :type="passwordFieldType"
            placeholder="Confirme la nueva contraseña"
            v-model="password_confirmation"
          />
          <div
            v-if="errors && errors.errors && errors.errors.password_confirmation"
            class="invalid-feedback"
          >
            <div v-for="(err, idx) of errors.errors.password_confirmation" :key="idx">{{ err }}</div>
          </div>
          <div v-else-if="password_confirmation && password !== password_confirmation" class="invalid-feedback d-block">
            Las contraseñas no coinciden.
          </div>
        </div>
      </div>

      <div v-if="generalError" class="alert alert-danger mt-3 mb-0 small">
        <i class="bi bi-exclamation-triangle-fill mr-1"></i> {{ generalError }}
      </div>
    </div>
  </crud-modal>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import CrudModal from "../utiles/CrudModal.vue";

export default {
  name: "UsuarioPasswordModal",
  components: {
    CrudModal,
  },
  props: {
    show: { type: Boolean, default: false },
    user: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      password: "",
      password_confirmation: "",
      passwordFieldType: "password",
      passwordStrength: null,
      loading: false,
      errors: null,
      generalError: null,
    };
  },
  watch: {
    show(val) {
      if (val) {
        this.password = "";
        this.password_confirmation = "";
        this.passwordFieldType = "password";
        this.passwordStrength = null;
        this.errors = null;
        this.generalError = null;
      }
    },
  },
  methods: {
    togglePasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === "password" ? "text" : "password";
    },

    checkPasswordStrength() {
      const pwd = this.password;
      const minLength = 8;
      const hasUpperCase = /[A-Z]/.test(pwd);
      const hasLowerCase = /[a-z]/.test(pwd);
      const hasNumbers = /[0-9]/.test(pwd);
      const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

      const isValid =
        pwd.length >= minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumbers &&
        hasSpecialChars;

      let message = "";
      if (!pwd) {
        message = "La contraseña es obligatoria.";
      } else if (!hasUpperCase) {
        message = "Debe contener al menos una letra mayúscula.";
      } else if (!hasLowerCase) {
        message = "Debe contener al menos una letra minúscula.";
      } else if (!hasNumbers) {
        message = "Debe contener al menos un número.";
      } else if (!hasSpecialChars) {
        message = "Debe contener al menos un carácter especial.";
      } else if (pwd.length < minLength) {
        message = `La contraseña debe tener al menos ${minLength} caracteres.`;
      } else {
        message = "Contraseña segura";
      }

      this.passwordStrength = { isValid, message };
    },

    guardarPassword() {
      this.errors = null;
      this.generalError = null;

      if (!this.password) {
        this.generalError = "Ingrese una nueva contraseña.";
        return;
      }
      if (this.password !== this.password_confirmation) {
        this.generalError = "Las contraseñas no coinciden.";
        return;
      }

      const userId = (this.user && (this.user.id_usuario || this.user.id));
      if (!userId) return;

      this.loading = true;
      axios
        .post(process.env.VUE_APP_API_URL + "usuario/password", {
          id: userId,
          id_usuario: userId,
          password: this.password,
        })
        .then((response) => {
          if (response && response.data && response.data.errno && response.data.errno > 0) {
            this.errors = response.data;
          } else {
            Swal.fire({
              title: "¡Contraseña actualizada!",
              text: "La contraseña ha sido cambiada correctamente.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
            this.$emit("password-updated");
            this.$emit("close");
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.generalError = typeof e.response.data.error === "string"
              ? e.response.data.error
              : (e.response.data.error.message || "Error al actualizar contraseña");
            if (e.response.data.errors) {
              this.errors = e.response.data;
            }
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar guardar la contraseña",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
